/**
 * Helpers for uploading files and folders to the Document Manager.
 * Normalizes FileList / DataTransfer / AntD Upload into
 * `{ file, relativePath }` entries and computes folders to create.
 */

const MAX_FOLDER_NAME = 50;

/** Truncates the folder name to the backend limit. */
export const sanitizeFolderName = (name) => {
  const trimmed = String(name || '').trim() || 'Sin título';
  return trimmed.length > MAX_FOLDER_NAME ? trimmed.slice(0, MAX_FOLDER_NAME) : trimmed;
};

/** Parent directory of a relativePath (`a/b/c.pdf` → `a/b`). */
export const dirnamePath = (relativePath) => {
  const path = String(relativePath || '').replace(/\\/g, '/');
  const idx = path.lastIndexOf('/');
  return idx <= 0 ? '' : path.slice(0, idx);
};

/**
 * Converts the Ant Design Upload fileList into normalized entries.
 * Uses `webkitRelativePath` when coming from folder selection.
 */
export const entriesFromUploadFileList = (fileList = []) =>
  (fileList || [])
    .map((item) => {
      const file = item?.originFileObj || item;
      if (!file) return null;
      const relativePath = (
        item.webkitRelativePath ||
        file.webkitRelativePath ||
        item.name ||
        file.name ||
        ''
      ).replace(/\\/g, '/');
      return { file, relativePath, uid: item.uid || file.uid };
    })
    .filter(Boolean);

/**
 * Reads a DataTransfer (drag & drop) including folders via
 * `webkitGetAsEntry` (Chrome/Edge/Safari). Falls back to flat files.
 *
 * @returns {Promise<Array<{file:File, relativePath:string}>>}
 */
export async function entriesFromDataTransfer(dataTransfer) {
  if (!dataTransfer) return [];

  const items = Array.from(dataTransfer.items || []);
  const hasEntries = items.some((it) => typeof it.webkitGetAsEntry === 'function');

  if (!hasEntries) {
    return Array.from(dataTransfer.files || []).map((file) => ({
      file,
      relativePath: (file.webkitRelativePath || file.name || '').replace(/\\/g, '/'),
    }));
  }

  const results = [];
  const emptyDirs = new Set();

  const readAllEntries = (reader) =>
    new Promise((resolve, reject) => {
      const acc = [];
      const readBatch = () => {
        reader.readEntries((batch) => {
          if (!batch.length) {
            resolve(acc);
            return;
          }
          acc.push(...batch);
          readBatch();
        }, reject);
      };
      readBatch();
    });

  const traverse = async (entry, pathPrefix) => {
    if (!entry) return;

    if (entry.isFile) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      const relativePath = pathPrefix ? `${pathPrefix}/${file.name}` : file.name;
      results.push({ file, relativePath: relativePath.replace(/\\/g, '/') });
      return;
    }

    if (entry.isDirectory) {
      const nextPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
      emptyDirs.add(nextPrefix.replace(/\\/g, '/'));
      const reader = entry.createReader();
      const children = await readAllEntries(reader);
      if (children.length === 0) {
        // Empty folder: still created (no files)
        return;
      }
      for (const child of children) {
        await traverse(child, nextPrefix);
      }
    }
  };

  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      await traverse(entry, '');
    } else {
      const file = item.getAsFile?.();
      if (file) {
        results.push({
          file,
          relativePath: (file.webkitRelativePath || file.name || '').replace(/\\/g, '/'),
        });
      }
    }
  }

  // Ensure empty folders are represented (marker without file).
  emptyDirs.forEach((dirPath) => {
    const hasFileInside = results.some(
      (r) => r.relativePath === dirPath || r.relativePath.startsWith(`${dirPath}/`),
    );
    if (!hasFileInside) {
      results.push({ file: null, relativePath: `${dirPath}/`, isEmptyDir: true });
    }
  });

  return results;
}

/**
 * Unique folder paths sorted shallowest to deepest
 * (so parents are created before children).
 */
export const collectDirectoryPaths = (entries = []) => {
  const dirs = new Set();

  entries.forEach((entry) => {
    let path = String(entry.relativePath || '').replace(/\\/g, '/');
    if (entry.isEmptyDir || path.endsWith('/')) {
      path = path.replace(/\/+$/, '');
      if (path) dirs.add(path);
      return;
    }
    const dir = dirnamePath(path);
    if (!dir) return;
    const parts = dir.split('/');
    let acc = '';
    parts.forEach((part) => {
      acc = acc ? `${acc}/${part}` : part;
      dirs.add(acc);
    });
  });

  return Array.from(dirs).sort(
    (a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b),
  );
};

/**
 * UI summary: file count and detected root folders.
 */
export const summarizeEntries = (entries = []) => {
  const files = entries.filter((e) => e.file && !e.isEmptyDir);
  const dirs = collectDirectoryPaths(entries);
  const rootFolders = new Set(dirs.map((d) => d.split('/')[0]).filter(Boolean));
  return {
    fileCount: files.length,
    folderCount: rootFolders.size,
    totalBytes: files.reduce((sum, e) => sum + (e.file?.size || 0), 0),
  };
};
